import { createContext, useContext, useReducer, useCallback, useMemo } from 'react'
import { toastDefaults, toastConfig, TOAST_TYPES } from '../components/ui/ScoopToast/ToastRegistry'

// =============================================================================
// ID Generator
// =============================================================================
let instanceCounter = 0
function generateToastId() {
    return `toast_${++instanceCounter}_${Date.now()}`
}

// =============================================================================
// Context
// =============================================================================
const ToastContext = createContext(null)

// =============================================================================
// Reducer
// =============================================================================
const initialState = {
    toasts: {}, // Active toasts by ID: { [id]: { message, type, duration, ... } }
    queue: [], // Queued toasts waiting to display
}

function toastReducer(state, action) {
    switch (action.type) {
        case 'ADD_TOAST': {
            const { id, toast } = action.payload
            const activeCount = Object.keys(state.toasts).length

            // If exceeds max visible, queue it
            if (activeCount >= toastConfig.maxVisible) {
                return {
                    ...state,
                    queue: [...state.queue, { id, ...toast }],
                }
            }

            return {
                ...state,
                toasts: {
                    ...state.toasts,
                    [id]: { ...toast, createdAt: Date.now() },
                },
            }
        }

        case 'REMOVE_TOAST': {
            const { [action.payload]: removed, ...remaining } = state.toasts

            // Promote from queue if available
            if (state.queue.length > 0) {
                const [next, ...restQueue] = state.queue
                return {
                    toasts: {
                        ...remaining,
                        [next.id]: { ...next, createdAt: Date.now() },
                    },
                    queue: restQueue,
                }
            }

            return { ...state, toasts: remaining }
        }

        case 'CLEAR_ALL':
            return initialState

        case 'CLEAR_QUEUE':
            return { ...state, queue: [] }

        default:
            return state
    }
}

// =============================================================================
// Provider
// =============================================================================
export function ToastProvider({ children }) {
    const [state, dispatch] = useReducer(toastReducer, initialState)

    const show = useCallback((message, type = TOAST_TYPES.INFO, options = {}) => {
        const id = generateToastId()
        const defaults = toastDefaults[type] || toastDefaults[TOAST_TYPES.INFO]

        dispatch({
            type: 'ADD_TOAST',
            payload: {
                id,
                toast: {
                    message,
                    type,
                    ...defaults,
                    ...options,
                },
            },
        })

        return id // Return ID for programmatic dismissal
    }, [])

    const hide = useCallback((id) => {
        dispatch({ type: 'REMOVE_TOAST', payload: id })
    }, [])

    const clearAll = useCallback(() => {
        dispatch({ type: 'CLEAR_ALL' })
    }, [])

    const value = useMemo(
        () => ({
            toasts: state.toasts,
            queue: state.queue,
            show,
            hide,
            clearAll,
        }),
        [state.toasts, state.queue, show, hide, clearAll]
    )

    return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
}

// =============================================================================
// Internal Hook (used by useToast.js)
// =============================================================================
export function useToastContext() {
    const context = useContext(ToastContext)
    if (!context) {
        throw new Error('useToastContext must be used within ToastProvider')
    }
    return context
}

// =============================================================================
// Ref System for Non-React Contexts (API interceptors)
// =============================================================================
let toastRef = null

export const setToastRef = (ref) => {
    toastRef = ref
}

export const getToastRef = () => toastRef
